import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Check, XCircle, ArrowRightLeft, RotateCcw, Package, Clock,
  CheckCircle2, Boxes, Paperclip,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/input";
import { DialogModal } from "../../components/ui/dialogModal";
import { cn } from "../../utils/tailwindUtils";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { ASSET_REQUEST, ASSET_REGISTRY } from "../../constants/api";
import { ASSET_REQUEST_TYPES, assetReqTypeClass } from "../../constants/assetRequest";
import {
  StatusBadge, PriorityBadge, TypeBadge, Timeline, DetailRows, fmtDateTime,
} from "./AssetRequestPieces";

/* Admin request detail. Drives the full workflow:
   Pending → approve/reject · Approved → issue an asset · Issued → mark returned.
   Admin can also reclassify the request type. */

const AssetRequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetch, update } = useApiMethods();

  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [issueOpen, setIssueOpen] = useState(false);

  // Gated by the System Admin role (no separate user-permission entry exists).
  const editPermission = JSON.parse(sessionStorage.getItem("is_system_admin") || "false")
    || JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ASSET_REQUEST, { id });
      if (res?.status === "success") {
        // Backend paginates: data = { result: [...] }.
        const arr = Array.isArray(res.data) ? res.data : (res.data?.result || []);
        setReq(arr[0] || null);
      } else ToastMsg("error", res?.message || "Could not load request.");
    } catch (e) {
      ToastMsg("error", e.message);
    } finally {
      setLoading(false);
    }
    // `fetch` is a new ref every render — depend only on `id`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const patch = async (payload, successMsg) => {
    try {
      const res = await update(ASSET_REQUEST, { id, ...payload });
      if (res?.status === "success") {
        ToastMsg("success", successMsg || res.message);
        await load();
        setNote("");
        return true;
      }
      ToastMsg("error", res?.message || "Action failed.");
      return false;
    } catch (e) {
      ToastMsg("error", e.message);
      return false;
    }
  };

  const approve = () => patch({ status: "Approved", decision_note: note || "Approved" }, "Request approved.");
  const reject = () => patch({ status: "Rejected", decision_note: note || "Rejected" }, "Request rejected.");
  const markReturned = () => patch({ status: "Returned" }, "Asset marked returned.");
  const changeType = (t) => { if (t !== req?.request_type) patch({ request_type: t }, `Reclassified to ${t}.`); };
  const issue = async (assetId) => {
    const ok = await patch({ status: "Issued", assigned_asset: assetId }, "Asset issued.");
    if (ok) setIssueOpen(false);
  };

  if (loading && !req) {
    return (
      <ContainerCard>
        <div className="h-[calc(100vh-100px)] dark:bg-gray-900 flex items-center justify-center rounded-md">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary"></div>
        </div>
      </ContainerCard>
    );
  }
  if (!req) {
    return (
      <ContainerCard>
        <div className="p-6">
          <BackBtn navigate={navigate} />
          <p className="mt-4 text-sm text-muted-foreground">Request not found.</p>
        </div>
      </ContainerCard>
    );
  }

  const hasAsset = req.assigned_asset_name || req.assigned_asset_tag;

  return (
    <ContainerCard>
      <div className="mx-auto p-3">
        <BackBtn navigate={navigate} />

        <div className="mt-3 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 items-start">
          {/* main column */}
          <div className="flex flex-col gap-4">
            {/* summary card */}
            <Card>
              <div className="flex items-start gap-3.5">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-sidebar-primary/10 text-sidebar-primary">
                  <Package className="h-6 w-6" />
                </span>
                <div className="flex-1">
                  <div className="mb-1.5 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-sidebar-primary">{req.request_no || `#${req.id}`}</span>
                    <TypeBadge type={req.request_type} full />
                    <PriorityBadge priority={req.priority} />
                    <StatusBadge status={req.status} />
                  </div>
                  <h1 className="text-xl font-bold leading-tight">{req.subject || `${req.category_name || "Asset"} Request`}</h1>
                  <p className="text-xs text-muted-foreground">
                    {req.category_name} · by {req.requester_name} ({req.employee_id}) · {req.department}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Description</p>
                <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">{req.justification || "—"}</p>
              </div>

              {Array.isArray(req.attachments) && req.attachments.length > 0 && (
                <div className="mt-4">
                  <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Attachments</p>
                  <div className="flex flex-wrap gap-2">
                    {req.attachments.map((a, i) => {
                      const url = typeof a === "string" ? a : a?.url;
                      const name = (typeof a === "object" && a?.name) || `Attachment ${i + 1}`;
                      return (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-2.5 py-1.5 text-xs text-sidebar-primary hover:underline">
                          <Paperclip className="h-3.5 w-3.5" />
                          <span className="max-w-[160px] truncate">{name}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {req.decision_note && (
                <div className="mt-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3.5">
                  <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Admin Decision Note</p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{req.decision_note}</p>
                </div>
              )}

              {hasAsset && (
                <div className="mt-4 flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 p-3.5 dark:border-green-900/40 dark:bg-green-900/20">
                  <span className="grid h-10 w-10 place-items-center rounded-lg bg-green-100 text-green-600 dark:bg-green-900/40 dark:text-green-300">
                    <Package className="h-5 w-5" />
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{req.assigned_asset_name || "Assigned asset"}</p>
                    <p className="text-xs text-muted-foreground">{req.assigned_asset_tag}</p>
                  </div>
                  <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300">Issued</span>
                </div>
              )}
            </Card>

            {/* action panels (gated by edit permission) */}
            {editPermission && req.status === "Pending" && (
              <Card>
                <h3 className="mb-3 text-base font-semibold">Review Decision</h3>
                <p className="mb-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300">Note (optional)</p>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Reason for approval / rejection…" />
                <div className="mt-3 flex gap-2.5">
                  <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={approve}>
                    <Check className="h-4 w-4" /> Approve Request
                  </Button>
                  <Button variant="destructive" onClick={reject}>
                    <XCircle className="h-4 w-4" /> Reject
                  </Button>
                </div>
              </Card>
            )}

            {editPermission && req.status === "Approved" && (
              <Card className="flex items-center gap-4">
                <div className="flex-1">
                  <h3 className="text-base font-semibold">Ready to Issue</h3>
                  <p className="text-sm text-muted-foreground">
                    Allocate a {(req.category_name || "asset").toLowerCase()} to {req.requester_name}.
                  </p>
                </div>
                <Button onClick={() => setIssueOpen(true)}>
                  <ArrowRightLeft className="h-4 w-4" /> Issue Asset
                </Button>
              </Card>
            )}

            {editPermission && req.status === "Issued" && (
              <Card className="flex items-center gap-4">
                <div className="flex-1">
                  <h3 className="text-base font-semibold">Asset In Use</h3>
                  <p className="text-sm text-muted-foreground">Mark returned when the employee hands it back.</p>
                </div>
                <Button variant="outline" onClick={markReturned}>
                  <RotateCcw className="h-4 w-4" /> Mark Returned
                </Button>
              </Card>
            )}
          </div>

          {/* side column */}
          <div className="flex flex-col gap-4">
            {editPermission && (
              <Card>
                <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">Issue Type</h4>
                <div className="flex flex-col gap-1.5">
                  {ASSET_REQUEST_TYPES.map((t) => {
                    const sel = req.request_type === t;
                    return (
                      <button key={t} onClick={() => changeType(t)}
                        className={cn(
                          "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors",
                          sel
                            ? cn("border-transparent", assetReqTypeClass(t))
                            : "border-gray-100 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:border-gray-200"
                        )}>
                        <span className="h-2 w-2 rounded-full bg-current" />
                        <span className="flex-1">{t}</span>
                        {sel && <Check className="h-4 w-4" />}
                      </button>
                    );
                  })}
                </div>
              </Card>
            )}

            <Card>
              <h4 className="mb-3 text-base font-bold uppercase tracking-wide text-muted-foreground">Details</h4>
              <DetailRows
                rows={[
                  ["Subject", req.subject || "—"],
                  ["Issue Type", req.request_type || "—"],
                  ["Requester", req.requester_name || "—"],
                  ["Employee ID", req.employee_id || "—"],
                  ["Department", req.department || "—"],
                  ["Category", req.category_name || "—"],
                  ["Submitted", fmtDateTime(req.created_at)],
                  ["Approved", fmtDateTime(req.approved_at)],
                  ["Issued", fmtDateTime(req.issued_at)],
                  ["Asset", req.assigned_asset_tag || req.assigned_asset_name || "—"],
                ]}
              />
            </Card>

            <Card>
              <h4 className="mb-3 text-xs font-bold uppercase tracking-wide text-muted-foreground">Timeline</h4>
              <Timeline log={req.approval_log || []} />
            </Card>
          </div>
        </div>
      </div>

      {issueOpen && (
        <IssueModal req={req} onClose={() => setIssueOpen(false)} onIssue={issue} />
      )}
    </ContainerCard>
  );
};

/* --------------------------------- bits ---------------------------------- */

const Card = ({ children, className }) => (
  <div className={cn(
    "rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-5",
    className
  )}>{children}</div>
);

const BackBtn = ({ navigate }) => (
  <button
    onClick={() => navigate("/assetmanagement/assetrequest")}
    className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-gray-700"
  >
    <ArrowLeft className="h-4 w-4" /> Back to requests
  </button>
);

/* Issue modal — pick a registry asset of the matching category to allocate. */
const IssueModal = ({ req, onClose, onIssue }) => {
  const { fetch } = useApiMethods();
  const [assets, setAssets] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(ASSET_REGISTRY, { category: req.category, drop_down: "True" });
        if (res?.status === "success") {
          // drop_down returns a flat array; paginated calls return { result: [...] }.
          const list = Array.isArray(res.data) ? res.data : (res.data?.result || []);
          setAssets(list);
          setSel(list[0]?.id ?? null);
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [req.category]);

  return (
    <DialogModal
      open
      onClose={onClose}
      onSave={() => onIssue(sel)}
      title={`Issue ${req.category_name || "asset"} to ${req.requester_name}`}
      saveLabel="Issue Asset"
      width="480px"
      hideSaveButton={!sel}
    >
      {loading ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Loading inventory…</p>
      ) : assets.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
          <Boxes className="h-8 w-8 opacity-40" />
          <p className="text-sm">No {(req.category_name || "asset").toLowerCase()} in inventory. Add one first.</p>
        </div>
      ) : (
        <div className="flex max-h-72 flex-col gap-2 overflow-y-auto">
          {assets.map((a) => (
            <button key={a.id} onClick={() => setSel(a.id)}
              className={cn(
                "flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-colors",
                sel === a.id ? "border-sidebar-primary bg-sidebar-primary/5" : "border-gray-100 dark:border-gray-800"
              )}>
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-sidebar-primary/10 text-sidebar-primary">
                <Package className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{a.asset_name || a.model_name}</span>
                <span className="block text-xs text-muted-foreground">{a.asset_id} · {a.serial_no || ""}</span>
              </span>
              {sel === a.id && <CheckCircle2 className="h-5 w-5 text-sidebar-primary" />}
            </button>
          ))}
        </div>
      )}
    </DialogModal>
  );
};

export default AssetRequestDetail;
